// Settings → Tools panel. Lists installed tools in the active org
// with their bound container + nest, and lets a tools:install holder
// install a new tool from a manifest JSON file.
//
// Tools are first-class extensions bound to a nest with a dedicated
// storage container. See apps/api/src/routes/tools.ts for the install
// semantics + docs/api-howto.md (forthcoming §21) for the manifest
// format. Server-side cap checks are authoritative; if the caller
// lacks the cap, listing / install fails with 403 / 423 surfaced
// via the toast.fromError dispatcher.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, apiFetch, apiList, apiSingle } from "../lib/api.js";
import { copyToClipboard } from "../lib/clipboard.js";
import { toast } from "./Toast.js";
import { Plus, Trash2, Box, Eye, FileText, FileJson, Pencil, Download, Copy, X, History } from "lucide-react";
import { CopyShortUrlButton } from "./CopyShortUrlButton.js";
import { AuditHistoryDrawer } from "./AuditHistoryDrawer.js";
import { NATIVE_TOOL_TYPES } from '../lib/native-tool-types.js';
// CTSK-347 — manifest landingPath: rows carry the stash on
// t.settings.landingPath; it wins over the native-type page for the
// Open action (absolute URLs open in a new tab).
import { settingsLandingPath, isExternalToolUrl } from '../lib/tool-navigation.js';
import { ToolManifestModal, downloadTextFile, type ManifestPayload } from './ToolManifestModal.js';

interface Tool {
  id: string;
  nestSlug: string | null;
  nestCode: string | null;
  nestName: string | null;
  slug: string;
  name: string;
  description: string | null;
  type: string;
  version: string;
  registryContainerId: string;
  registryContainerName: string | null;
  registryContainerSlug: string | null;
  registryPath: string;
  registryPublic: boolean;
  settings: Record<string, unknown>;
  isActive: boolean;
  installedAt: string;
}

interface Nest {
  id: string;
  slug: string;
  code: string;
  name: string;
  description: string | null;
}

interface InstallSummary {
  dryRun: boolean;
  toolId: string | null;
  nest: { slug: string; code: string; name: string };
  container: { id: string; name: string; created: boolean };
  defaults: {
    tags: { inserted: number; skipped: number };
    rules: { inserted: number; skipped: number };
    customProperties: { inserted: number; skipped: number };
    fileWorkflows: { inserted: number; skipped: number; note?: string };
    workflows: { inserted: number; skipped: number; note?: string };
  };
  warnings: string[];
}

// Tool types captain-tasko ships native UI for. When the table row's
// type matches, the panel surfaces a "Open" link to the tool's native
// page. Otherwise falls back to "Open container" — the operator
// browses the bound container in the documents UI. Add types here as
// native pages are built.
// NATIVE_TOOL_TYPES moved to ../lib/native-tool-types.ts (0.1.8
// follow-up) so the sidebar Nests panel and this panel share one
// source of truth for which tools have a captain-tasko-native page.

export function ToolsPanel({
  onNavigate,
  nestSlug = null,
}: {
  onNavigate?: (path: string) => void;
  // CTSK-348 — filter the table to one nest. Fed by the sidebar
  // nest-card Tools cell deep-link (/settings?section=tools&
  // nestSlug=<slug>, emitted since 0.1.8) via router → Settings.
  nestSlug?: string | null;
}) {
  const qc = useQueryClient();
  const [installOpen, setInstallOpen] = useState(false);
  // CTSK-348 — active nest filter. Local state so the chip's clear
  // button works even without onNavigate; re-synced from the prop so
  // clicking another nest's tile while this panel is already mounted
  // (pushState-only navigation, no remount) re-targets the filter —
  // same lesson as Settings' section re-sync (CTSK-346 / ctsk-056).
  const [nestFilter, setNestFilter] = useState<string | null>(nestSlug);
  useEffect(() => {
    setNestFilter(nestSlug);
  }, [nestSlug]);
  const clearNestFilter = () => {
    // Prefer cleaning the URL (so refresh / back don't resurrect the
    // filter); fall back to local state when no navigator was passed.
    if (onNavigate) onNavigate("/settings?section=tools");
    else setNestFilter(null);
  };
  const [manifestText, setManifestText] = useState("");
  const [selectedNestSlug, setSelectedNestSlug] = useState("");
  const [previewSummary, setPreviewSummary] = useState<InstallSummary | null>(
    null,
  );
  const [parseError, setParseError] = useState<string | null>(null);
  // CTSK-292 — which tool's manifest modal is open, and in which mode.
  const [manifestModal, setManifestModal] = useState<{
    tool: Tool;
    mode: "view" | "edit";
  } | null>(null);
  // Fast-follow (CTSK-389) — no per-resource /tools/:slug detail route
  // exists (confirmed against router.tsx), so History opens the same
  // AuditHistoryDrawer off the row action cluster, same as the
  // manifest-modal buttons above it.
  const [historyTool, setHistoryTool] = useState<Tool | null>(null);

  const toolsQ = useQuery({
    queryKey: ["tools"],
    queryFn: () => apiList<Tool>("/tools"),
  });

  // CTSK-292 — resolve the caller's caps so the Edit affordance can be
  // hidden without tools:configure. Server cap gates stay authoritative;
  // this is display-only (mirrors how the server would 403 anyway).
  const sessionQ = useQuery({
    queryKey: ["auth-session-caps"],
    queryFn: () =>
      apiSingle<{ session: { capabilities: string[] } }>("/auth/session"),
  });
  const canConfigure =
    sessionQ.data?.session.capabilities.includes("tools:configure") ?? false;

  // CTSK-292 — row-level Download / Copy fetch the manifest on demand
  // (no modal). Both funnel through GET /tools/:id/manifest so the
  // bytes are identical to what the modal shows.
  const fetchManifest = (id: string) =>
    apiSingle<ManifestPayload>(`/tools/${id}/manifest`);

  const handleDownloadManifest = async (t: Tool) => {
    try {
      const payload = await fetchManifest(t.id);
      downloadTextFile(
        `${t.slug}.manifest.json`,
        JSON.stringify(payload.manifest, null, 2),
      );
      toast.success(`${t.slug}.manifest.json downloaded`);
    } catch (err) {
      toast.fromError(err, "Failed to download manifest");
    }
  };

  const handleCopyManifest = async (t: Tool) => {
    try {
      const payload = await fetchManifest(t.id);
      const ok = await copyToClipboard(
        JSON.stringify(payload.manifest, null, 2),
      );
      if (ok) toast.success(`${t.slug} manifest JSON copied`);
      else toast.error("Copy failed — open View and copy from the textarea.");
    } catch (err) {
      toast.fromError(err, "Failed to copy manifest");
    }
  };

  const nestsQ = useQuery({
    queryKey: ["nests"],
    queryFn: () => apiList<Nest>("/nests"),
  });

  const installMut = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) => {
      let manifest: unknown;
      try {
        manifest = JSON.parse(manifestText);
      } catch (err) {
        throw new Error(
          `Manifest is not valid JSON: ${(err as Error).message}`,
        );
      }
      return apiSingle<InstallSummary>("/tools", {
        method: "POST",
        body: { nestSlug: selectedNestSlug, manifest, dryRun },
      });
    },
    onSuccess: (data, vars) => {
      if (vars.dryRun) {
        setPreviewSummary(data);
        setParseError(null);
        return;
      }
      toast.success(`Tool installed in ${data.nest.name}`);
      setInstallOpen(false);
      setManifestText("");
      setSelectedNestSlug("");
      setPreviewSummary(null);
      qc.invalidateQueries({ queryKey: ["tools"] });
    },
    onError: (err: unknown) => {
      if (
        err instanceof Error &&
        err.message.startsWith("Manifest is not valid JSON")
      ) {
        setParseError(err.message);
        return;
      }
      toast.fromError(err, "Install failed");
    },
  });

  const uninstallMut = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/tools/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Tool uninstalled");
      qc.invalidateQueries({ queryKey: ["tools"] });
    },
    onError: (err: unknown) => toast.fromError(err, "Failed to uninstall"),
  });

  if (toolsQ.isError) {
    const err = toolsQ.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div
          style={{
            padding: 20,
            background: "var(--surface-sunken)",
            borderRadius: "var(--radius-md)",
            color: "var(--text-muted)",
            fontSize: 13,
          }}
        >
          Viewing tools requires the <code>tools:view</code> capability.
        </div>
      );
    }
    return (
      <div style={{ color: "var(--danger-primary, #B3261E)" }}>
        Failed to load tools.
      </div>
    );
  }

  const tools = toolsQ.data?.data ?? [];
  const nests = nestsQ.data?.data ?? [];

  // CTSK-348 — apply the nest filter. Tool rows carry nestSlug
  // first-class, so this is a pure client-side filter over the same
  // /tools payload — no query change.
  const visibleTools = nestFilter
    ? tools.filter((t) => t.nestSlug === nestFilter)
    : tools;
  // Chip label: prefer the nest's display name (nests list first,
  // then any matching tool row's denormalized nestName); fall back to
  // the raw slug so a stale or mistyped deep-link still renders an
  // honest chip instead of a blank.
  const filterNestName = nestFilter
    ? nests.find((n) => n.slug === nestFilter)?.name ??
      tools.find((t) => t.nestSlug === nestFilter)?.nestName ??
      nestFilter
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <section
        style={{
          background: "var(--surface-raised)",
          padding: 20,
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16 }}>Tools</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* CTSK-289 — panel-header help link. Mirrors the Settings →
                Bundles pattern (CTSK-286): plain anchor, muted text,
                full-page nav is fine for /help/*. */}
            <a
              href="/help/tools"
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                textDecoration: "none",
              }}
              title="What are tools?"
            >
              Help ↗
            </a>
            {!installOpen && (
              <button
                type="button"
                onClick={() => setInstallOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  fontSize: 12,
                  border: "1px solid var(--border-strong)",
                  borderRadius: 6,
                  background: "var(--surface-raised)",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                }}
              >
                <Plus size={12} /> Install tool
              </button>
            )}
          </div>
        </div>
        <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 0 }}>
          {nestFilter
            ? `${visibleTools.length} of ${tools.length} installed`
            : `${tools.length} installed`}{" "}
          · Tools are extensions bound to a nest with a dedicated storage
          container for their registry.
        </p>

        {/* CTSK-348 — nest-filter chip. Rendered when the sidebar
            nest-card Tools cell deep-linked here with &nestSlug=; the
            × clears the filter (and cleans the URL when a navigator
            is available). */}
        {nestFilter && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 8px 3px 10px",
              marginBottom: 12,
              fontSize: 12,
              borderRadius: 999,
              border: "1px solid var(--border-strong)",
              background: "var(--surface-sunken)",
              color: "var(--text-secondary)",
            }}
          >
            Filtered by nest:{" "}
            <strong style={{ color: "var(--text-primary)" }}>
              {filterNestName}
            </strong>
            <button
              type="button"
              onClick={clearNestFilter}
              title="Clear nest filter"
              aria-label="Clear nest filter"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: 2,
                border: "none",
                background: "transparent",
                color: "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Install form */}
        {installOpen && (
          <div
            style={{
              background: "var(--surface-sunken)",
              padding: 16,
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border-subtle)",
              marginBottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div>
              <label
                style={{
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Nest
              </label>
              <select
                value={selectedNestSlug}
                onChange={(e) => setSelectedNestSlug(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  fontSize: 13,
                  border: "1px solid var(--border-strong)",
                  borderRadius: 4,
                  background: "var(--surface-raised)",
                }}
              >
                <option value="">— pick a nest —</option>
                {nests.map((n) => (
                  <option key={n.id} value={n.slug}>
                    {n.code} · {n.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Manifest JSON
              </label>
              <textarea
                value={manifestText}
                onChange={(e) => {
                  setManifestText(e.target.value);
                  setParseError(null);
                  setPreviewSummary(null);
                }}
                placeholder='{"$schema":"captain-tasko-tool","tool":{...}}'
                rows={12}
                spellCheck={false}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  fontSize: 12,
                  fontFamily: "var(--font-mono)",
                  border: "1px solid var(--border-strong)",
                  borderRadius: 4,
                  resize: "vertical",
                }}
              />
              {parseError && (
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: 11,
                    color: "var(--danger-primary, #B3261E)",
                  }}
                >
                  {parseError}
                </p>
              )}
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: 11,
                  color: "var(--text-muted)",
                }}
              >
                Paste the JSON manifest. For YAML tools, convert first:{" "}
                <code>yq -o json &lt; manifest.yaml</code>
              </p>
            </div>

            {/* Preview summary from dryRun */}
            {previewSummary && (
              <div
                style={{
                  padding: 12,
                  background: "var(--surface-raised)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <strong style={{ color: "var(--accent-primary)" }}>
                  Preview
                </strong>
                <ul
                  style={{
                    margin: "6px 0 0 16px",
                    padding: 0,
                    lineHeight: 1.6,
                  }}
                >
                  <li>
                    Nest: <code>{previewSummary.nest.slug}</code> ·{" "}
                    {previewSummary.nest.name}
                  </li>
                  <li>
                    Container: <code>{previewSummary.container.name}</code>
                    {previewSummary.container.created
                      ? " (will be created)"
                      : " (existing — will bind)"}
                  </li>
                  <li>
                    Tags: +{previewSummary.defaults.tags.inserted} new,{" "}
                    {previewSummary.defaults.tags.skipped} already exist
                  </li>
                  <li>
                    Rules: +{previewSummary.defaults.rules.inserted} new,{" "}
                    {previewSummary.defaults.rules.skipped} already exist
                  </li>
                  <li>
                    Custom properties: +
                    {previewSummary.defaults.customProperties.inserted} new,{" "}
                    {previewSummary.defaults.customProperties.skipped} already
                    exist
                  </li>
                </ul>
                {previewSummary.warnings.length > 0 && (
                  <ul
                    style={{
                      margin: "6px 0 0 16px",
                      padding: 0,
                      color: "var(--warning-primary)",
                      lineHeight: 1.6,
                    }}
                  >
                    {previewSummary.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => installMut.mutate({ dryRun: true })}
                disabled={
                  installMut.isPending ||
                  !manifestText.trim() ||
                  !selectedNestSlug
                }
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "transparent",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-strong)",
                  borderRadius: 4,
                  cursor: installMut.isPending ? "wait" : "pointer",
                }}
              >
                Preview (dry run)
              </button>
              <button
                type="button"
                onClick={() => installMut.mutate({ dryRun: false })}
                disabled={
                  installMut.isPending ||
                  !manifestText.trim() ||
                  !selectedNestSlug
                }
                style={{
                  // CTSK-288 — equal visual weight with Preview/Cancel:
                  // no flex-grow stretch, no bolder text. Self-colored
                  // 1px border keeps box height identical to the
                  // bordered siblings; accent background stays the
                  // primary marker.
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "var(--accent-primary)",
                  color: "#fff",
                  border: "1px solid var(--accent-primary)",
                  borderRadius: 4,
                  cursor: installMut.isPending ? "wait" : "pointer",
                }}
              >
                Install
              </button>
              <button
                type="button"
                onClick={() => {
                  setInstallOpen(false);
                  setManifestText("");
                  setSelectedNestSlug("");
                  setPreviewSummary(null);
                  setParseError(null);
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "transparent",
                  color: "var(--text-muted)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {toolsQ.isLoading ? (
          <div style={{ color: "var(--text-muted)" }}>Loading tools…</div>
        ) : tools.length === 0 ? (
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: 13,
              fontStyle: "italic",
              padding: "12px 0",
            }}
          >
            No tools installed yet. Click "Install tool" to drop in a manifest.
          </div>
        ) : visibleTools.length === 0 ? (
          // CTSK-348 — tools exist, but none in the filtered nest
          // (deep-link from a 0-tool nest tile, or the last tool was
          // just uninstalled). Distinct copy from the true-empty state
          // so the operator isn't told the org has no tools at all.
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: 13,
              fontStyle: "italic",
              padding: "12px 0",
            }}
          >
            No tools installed in {filterNestName}. Clear the filter to see
            all {tools.length}, or click "Install tool" to add one here.
          </div>
        ) : (
          // Column widths tuned 0.1.8 follow-up so every column is
          // readable at viewport width:
          //   Name 340       — fits the strong + description sub-line
          //                    without truncating the most common
          //                    descriptions ("Catalog every external
          //                    data source…")
          //   Type 220       — fits the longest seeded type slugs
          //                    ("data-sources-registry" = 21 chars)
          //   Version 80     — "1.0.0" + a few chars of slack
          //   Nest 80        — code is ≤4 chars
          //   Container auto — takes the slack; nowrap+ellipsis
          //                    on the cell so long container names
          //                    truncate cleanly instead of pushing
          //                    actions off-screen
          //   Actions 250    — Open / Container / manifest quartet
          //                    (View / Edit / Download / Copy, CTSK-292)
          //                    / Trash icons
          <table className="grid" style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 340 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 80 }} />
              <col />
              <col style={{ width: 250 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Version</th>
                <th>Nest</th>
                <th>Container</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleTools.map((t) => {
                const nativePath = NATIVE_TOOL_TYPES[t.type];
                // CTSK-347 — landingPath (manifest-declared, stashed on
                // settings) wins over the native page; no landingPath +
                // no native page keeps the "Open container" fallback.
                const landingPath = settingsLandingPath(t.settings);
                const openUrl = landingPath ?? nativePath;
                const openExternal = openUrl != null && isExternalToolUrl(openUrl);
                return (
                  <tr key={t.id} style={{ opacity: t.isActive ? 1 : 0.55 }}>
                    <td
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={t.name}
                    >
                      <strong>{t.name}</strong>
                      {t.description && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-muted)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={t.description}
                        >
                          {t.description}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color: "var(--text-muted)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={t.type}
                    >
                      {t.type}
                    </td>
                    <td
                      style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}
                    >
                      {t.version}
                    </td>
                    <td style={{ fontSize: 12 }} title={t.nestName ?? ""}>
                      {t.nestCode ?? (
                        <span style={{ color: "var(--text-muted)" }}>—</span>
                      )}
                    </td>
                    <td
                      style={{
                        fontSize: 12,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontFamily: "var(--font-mono)",
                      }}
                      title={t.registryContainerName ?? ""}
                    >
                      <Box
                        size={11}
                        style={{ verticalAlign: "middle", marginRight: 4 }}
                      />
                      {t.registryContainerSlug ?? "—"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                        {/* CTSK-354 — copy the /ct/:slug short URL for this tool. */}
                        <CopyShortUrlButton shortPath={`/ct/${t.slug}`} label={t.name} />
                        {openUrl && onNavigate && (
                          <button
                            type="button"
                            title={
                              openExternal
                                ? 'Open the tool\'s landing URL (new tab)'
                                : landingPath
                                ? 'Open the tool\'s landing page'
                                : 'Open the tool\'s native UI'
                            }
                            onClick={() =>
                              openExternal
                                ? window.open(openUrl, '_blank', 'noopener,noreferrer')
                                : onNavigate(openUrl)
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 8px",
                              fontSize: 11,
                              background: "transparent",
                              border: "1px solid var(--border-strong)",
                              borderRadius: 4,
                              cursor: "pointer",
                              color: "var(--accent-primary)",
                            }}
                          >
                            <Eye size={11} /> Open
                          </button>
                        )}
                        {!openUrl && onNavigate && (
                          <button
                            type="button"
                            title="Browse the bound container in Documents"
                            onClick={() =>
                              onNavigate(
                                `/documents?container=${t.registryContainerSlug}`,
                              )
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "4px 8px",
                              fontSize: 11,
                              background: "transparent",
                              border: "1px solid var(--border-subtle)",
                              borderRadius: 4,
                              cursor: "pointer",
                              color: "var(--text-secondary)",
                            }}
                          >
                            <FileText size={11} /> Container
                          </button>
                        )}
                        {/* CTSK-292 — manifest quartet. View for every
                            tools:view holder; Edit only with
                            tools:configure (server gate authoritative). */}
                        <button
                          type="button"
                          title="View manifest JSON"
                          aria-label={`View manifest of ${t.name}`}
                          onClick={() =>
                            setManifestModal({ tool: t, mode: "view" })
                          }
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            padding: 0,
                            background: "transparent",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: 4,
                            cursor: "pointer",
                            color: "var(--text-secondary)",
                          }}
                        >
                          <FileJson size={12} />
                        </button>
                        {canConfigure && (
                          <button
                            type="button"
                            title="Edit manifest"
                            aria-label={`Edit manifest of ${t.name}`}
                            onClick={() =>
                              setManifestModal({ tool: t, mode: "edit" })
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 24,
                              height: 24,
                              padding: 0,
                              background: "transparent",
                              border: "1px solid var(--border-subtle)",
                              borderRadius: 4,
                              cursor: "pointer",
                              color: "var(--accent-primary)",
                            }}
                          >
                            <Pencil size={12} />
                          </button>
                        )}
                        <button
                          type="button"
                          title={`Download ${t.slug}.manifest.json`}
                          aria-label={`Download manifest of ${t.name}`}
                          onClick={() => void handleDownloadManifest(t)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            padding: 0,
                            background: "transparent",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: 4,
                            cursor: "pointer",
                            color: "var(--text-secondary)",
                          }}
                        >
                          <Download size={12} />
                        </button>
                        <button
                          type="button"
                          title="Copy manifest JSON to clipboard"
                          aria-label={`Copy manifest of ${t.name}`}
                          onClick={() => void handleCopyManifest(t)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            padding: 0,
                            background: "transparent",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: 4,
                            cursor: "pointer",
                            color: "var(--text-secondary)",
                          }}
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          type="button"
                          title={`View audit history for ${t.name}`}
                          aria-label={`View history of ${t.name}`}
                          onClick={() => setHistoryTool(t)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            padding: 0,
                            background: "transparent",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: 4,
                            cursor: "pointer",
                            color: "var(--text-secondary)",
                          }}
                        >
                          <History size={12} />
                        </button>
                        <button
                          type="button"
                          title="Uninstall"
                          onClick={() => {
                            if (
                              confirm(
                                `Uninstall ${t.name}? The bound container and any seeded tags / rules / properties stay in place — only the tool record is removed.`,
                              )
                            ) {
                              uninstallMut.mutate(t.id);
                            }
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            padding: 0,
                            background: "transparent",
                            border: "1px solid var(--border-subtle)",
                            borderRadius: 4,
                            cursor: "pointer",
                            color: "var(--danger-primary, #B3261E)",
                          }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section
        style={{
          background: "var(--surface-sunken)",
          padding: 16,
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          fontSize: 12,
          color: "var(--text-muted)",
        }}
      >
        <strong>About tools</strong>
        <p style={{ margin: "6px 0 0 0" }}>
          A tool is a first-class extension bound to a single nest with a
          dedicated storage container holding its <code>registry.yaml</code>. On
          install, the manifest's <code>defaults</code> block seeds tags, rules,
          and custom properties into the nest (idempotent — re-install skips
          existing rows).
        </p>
        <p style={{ margin: "6px 0 0 0" }}>
          See <code>docs/api-howto.md</code> §21 for the manifest schema and
          curl-driven install recipe.
        </p>
      </section>

      {/* CTSK-292 — per-tool manifest View / Edit modal */}
      {manifestModal && (
        <ToolManifestModal
          toolId={manifestModal.tool.id}
          toolName={manifestModal.tool.name}
          toolSlug={manifestModal.tool.slug}
          toolVersion={manifestModal.tool.version}
          mode={manifestModal.mode}
          canConfigure={canConfigure}
          onClose={() => setManifestModal(null)}
        />
      )}

      {/* Fast-follow (CTSK-389) — per-tool audit history drawer */}
      {historyTool && (
        <AuditHistoryDrawer
          resourceType="tool"
          resourceId={historyTool.id}
          onClose={() => setHistoryTool(null)}
        />
      )}
    </div>
  );
}
